import { supabase } from '@/integrations/supabase/client';
import { trackLavlahEvent, type LavlahEventName } from '@/utils/lavlahAnalytics';

/**
 * Shared helpers for the inline "Хурдан бүртгэл" (2-password anonymous signup)
 * used by Add Place, Edit Place, Talk post, Deal post, etc.
 * Returns a Mongolian error message, or null when valid.
 */
export function validateQuickPassword(password: string, confirm: string): string | null {
  if (!password || password.length < 6) return 'Нууц үг хамгийн багадаа 6 тэмдэгт байх ёстой.';
  if (password !== confirm) return 'Нууц үг таарахгүй байна.';
  return null;
}

const QUICK_CRED_KEY = 'lavlah.quick_cred';
const QUICK_IP_KEY = 'lavlah.quick_ip';
const QUICK_EMAIL_KEY = 'lavlah.quick_email';

/**
 * SECURITY: We intentionally do NOT persist the raw password to localStorage
 * — a single XSS or malicious extension would otherwise exfiltrate the
 * credential. Auto-login for returning quick-signup visitors instead relies
 * on the existing Supabase session cookie (refresh token) which the SDK
 * stores in an httpOnly-equivalent manner and rotates automatically.
 */
export function clearQuickCred() {
  try {
    localStorage.removeItem(QUICK_CRED_KEY);
    localStorage.removeItem(QUICK_IP_KEY);
    localStorage.removeItem(QUICK_EMAIL_KEY);
  } catch {}
}

/**
 * Returning quick-signup users are re-authenticated via the Supabase refresh
 * token automatically (see `AuthContext`). This helper is kept as a no-op for
 * backwards compatibility with existing call sites.
 */
export async function tryAutoLoginQuickUser(): Promise<boolean> {
  // Clean up any legacy plaintext-credential entries from older builds.
  clearQuickCred();
  const { data } = await supabase.auth.getSession();
  return !!data?.session;
}

/**
 * Email-less quick signup. Creates a real Supabase account using a synthetic
 * email derived from a random UUID + the user-supplied password, so the
 * password the user typed actually becomes their account credential.
 *
 * TODO(account-linking): when a user later adds a real email/Google in Settings,
 * call supabase.auth.updateUser({ email }) to upgrade this synthetic account.
 */
export async function signUpQuick(
  feature: string,
  password: string,
): Promise<{ error?: string }> {
  const email = `lavlah-${crypto.randomUUID()}@guest.lavlah.mn`;
  // Admin-create a pre-confirmed user via edge function so Supabase does
  // NOT send a verification email to the unroutable @guest.lavlah.mn
  // address (no MX records → hard bounces from emailarray.com).
  const { data, error: fnErr } = await supabase.functions.invoke('quick-signup', {
    body: { email, password, feature },
  });
  if (fnErr) return { error: fnErr.message };
  if (data?.error) return { error: data.error };
  // Now sign the user in with the password they typed.
  const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
  if (signInErr) return { error: signInErr.message };
  // Persist only the synthetic email (no password) for UX-side account-recovery
  // hints. Session continuity is handled by Supabase's refresh-token cookie.
  try { localStorage.setItem(QUICK_EMAIL_KEY, email); } catch {}
  trackLavlahEvent('quick_signup_success' as LavlahEventName, { feature });
  return {};
}

/** @deprecated Use `signUpQuick(feature, password)` instead. Kept as a thin alias. */
export const signUpAnonymous = (feature: string, password = '') =>
  signUpQuick(feature, password);
